import { type Reducer } from "redux";
import {
  SET_ERROR,
  SET_LOADING,
  SET_SUCCESS,
  SET_WINDOW_WIDTH,
  SET_SUBSCRIPTION,
  SET_STRIPE_CLIENT_SECRET,
  SET_INFO,
  SET_DATE_RANGE,
  SET_SUBSCRIPTION_PLANS,
  SET_DEBUG,
  SET_IS_MOBILE,
  SET_CURRENCY,
  SET_CURRENCY_SUB_UNITS,
  SET_COUNTRY_CODE
} from "../actions/app";
import { type SubscriptionJavaDTO } from "@tootsweet/model/entities/Subscription";
import moment from "moment-timezone";
import type SubscriptionPlanDTO from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import type DateValueI from "../../model/DateValueI";
import CurrencySubUnitsMap from "../../model/CurrencySubUnitsMap";
import {
  DEFAULT_CURRENCY_SUB_UNITS,
  DEFAULT_CURRENCY
} from "@tootsweet/model/enums/Currencies";
import { DEFAULT_COUNTRY_CODE } from "../../utils/constants";

export interface AppStateI {
  isDebug: boolean;
  isLoading: boolean;
  isMobile: boolean;
  error: string;
  success: string;
  info: string;
  windowWidth: number;
  subscriptions: SubscriptionsMap;
  stripeClientSecret: string | null;
  dateRange: DateValueI;
  subscriptionPlans: SubscriptionPlanDTO[];
  countryCode: string;
  currency: string;
  currencySubUnits: CurrencySubUnitsMap;
}

export type SubscriptionsMap = Record<string, SubscriptionJavaDTO>;

const initialState: AppStateI = {
  isDebug: localStorage.getItem("saasDebug") === "true",
  isLoading: false,
  isMobile: false,
  error: "",
  success: "",
  info: "",
  windowWidth: window.innerWidth,
  subscriptions: {},
  stripeClientSecret: null,
  dateRange: {
    start: moment().subtract(1, "week").startOf("day").toDate(),
    end: moment().endOf("day").toDate()
  },
  subscriptionPlans: [],
  countryCode: DEFAULT_COUNTRY_CODE,
  currency: DEFAULT_CURRENCY,
  currencySubUnits: {
    DEFAULT_CURRENCY: DEFAULT_CURRENCY_SUB_UNITS
  }
};

const appReducer: Reducer<AppStateI> = (state = initialState, action: any) => {
  switch (action.type) {
    case SET_DEBUG:
      localStorage.setItem("saasDebug", `${action.isDebug}`);
      return {
        ...state,
        isDebug: action.isDebug
      };

    case SET_LOADING:
      return {
        ...state,
        isLoading: action.isLoading
      };

    case SET_IS_MOBILE:
      return {
        ...state,
        isMobile: action.isMobile
      };

    case SET_ERROR:
      return {
        ...state,
        error: action.error
      };

    case SET_SUCCESS:
      return {
        ...state,
        success: action.success
      };

    case SET_INFO:
      return {
        ...state,
        info: action.info
      };

    case SET_WINDOW_WIDTH:
      return {
        ...state,
        windowWidth: action.width
      };

    case SET_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          [action.account]: action.subscription
        }
      };

    case SET_STRIPE_CLIENT_SECRET:
      return {
        ...state,
        stripeClientSecret: action.secret
      };

    case SET_DATE_RANGE:
      const newDateRange = { ...action.dateRange };
      const sts = moment(action.dateRange.start).startOf("day").toDate();
      const ets = moment(action.dateRange.end).endOf("day").toDate();
      newDateRange.start = sts;
      newDateRange.end = ets;

      return {
        ...state,
        dateRange: newDateRange
      };

    case SET_SUBSCRIPTION_PLANS:
      return {
        ...state,
        subscriptionPlans: action.subscriptionPlans
      };

    case SET_CURRENCY:
      return {
        ...state,
        currency: action.currency
      };

    case SET_COUNTRY_CODE:
      return {
        ...state,
        countryCode: action.countryCode
      };

    case SET_CURRENCY_SUB_UNITS:
      return {
        ...state,
        currencySubUnits: action.currencySubUnits
      };
  }

  return state;
};

export default appReducer;
